import axios from "axios";
import type { BalancesResponse, AccountType, BalanceHistoryItem } from '../types/balances.types';
import { 
    createMockResponse, 
    generateMockHistory, 
    mockDelay,
    MOCK_RESPONSE 
} from '../mock/balances.mock';

const FORCE_MOCK = import.meta.env.VITE_USE_MOCK_BALANCES === 'true';

const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL || '/api/v1',
    timeout: 15000,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
});

if (import.meta.env.DEV) {
    api.interceptors.request.use(config => {
        console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
        return config;
    });
    
    api.interceptors.response.use(
        response => {
            console.log(`[API] Response:`, response.data);
            return response;
        },
        error => {
            console.error(`[API] Error:`, error.message);
            return Promise.reject(error);
        }
    );
}

export const balancesApi = {
    async getBalances(): Promise<BalancesResponse> {
        if (FORCE_MOCK) {
            console.log('[BalancesAPI] Using mock data');
            await mockDelay(400, 800);
            return createMockResponse();
        }

        try {
            const { data } = await api.get<BalancesResponse>('/balances');

            if (!data || !Array.isArray(data.data)) {
                console.warn('[BalancesAPI] Invalid response format, falling back to mock');
                return createMockResponse();
            }

            return {
                ...data,
                isMock: false
            };
        } catch (error) {
            console.warn('[BalancesAPI] Backend error, falling back to mock:', error);
            await mockDelay(200, 400);
            return createMockResponse();
        }
    },

    async getAccountHistory(
        organization: string,
        accountName: AccountType,
        currency: string,
        limit = 30
    ): Promise<BalanceHistoryItem[]> {
        if (FORCE_MOCK) {
            console.log('[BalancesAPI] Using mock history for:', organization, accountName, currency);
            await mockDelay(200, 500);
            
            return generateMockHistory(1000000, limit);
        }

        try {
            const { data } = await api.get(
                `/balances/history?org=${encodeURIComponent(organization)}&accountName=${accountName}&currency=${currency}&limit=${limit}`
            );

            if (!Array.isArray(data)) {
                console.warn('[BalancesAPI] Invalid history format');
                return generateMockHistory(1000000, limit);
            }

            return data;
        } catch (error) {
            console.warn('[BalancesAPI] Backend error, falling back to mock history:', error);
            return generateMockHistory(1000000, limit);
        }
    },

    forceMock(): BalancesResponse {
        return MOCK_RESPONSE;
    },

    isUsingMock(): boolean {
        return FORCE_MOCK || !import.meta.env.VITE_API_URL;
    }
};